import { WalkAndPunchApiClient } from 'src/proxy/api/walkandpunch-api.proxy';
import { Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AppFeature } from 'src/app/models/enums/app-feature.enum';

/** Availability of each backend-toggled feature for the current project. */
export type FeatureAccess = Record<AppFeature, boolean>;

const NO_ACCESS: FeatureAccess = {
  [AppFeature.SignedPunches]: false,
  [AppFeature.MyPunches]: false,
};

/**
 * Central store for backend-toggled features (menu options and routes).
 * Flags are reloaded every time the selected project changes.
 */
@Injectable({
  providedIn: 'root',
})
export class AccessControlService {
  private readonly accessState = signal<FeatureAccess>(NO_ACCESS);
  private readonly loadedState = signal(false);
  private readonly refresh$ = new Subject<void>();
  private readonly loaded$ = toObservable(this.loadedState);
  private requested = false;

  /** Current feature flags. */
  readonly access = this.accessState.asReadonly();
  /** Whether the flags for the current project have been resolved. */
  readonly loaded = this.loadedState.asReadonly();

  constructor(
    private documentProcessor: WalkAndPunchApiClient,
    private punchBagService: WalkAndPunchApiClient
  ) {
    this.refresh$
      .pipe(
        tap(() => this.loadedState.set(false)),
        switchMap(() => this.loadAccess()),
        takeUntilDestroyed()
      )
      .subscribe((access) => {
        this.accessState.set(access);
        this.loadedState.set(true);
      });
  }

  /** Reloads every feature flag; stale responses from previous projects are discarded. */
  refresh(): void {
    this.requested = true;
    this.refresh$.next();
  }

  /** Returns whether the feature is enabled with the flags currently loaded. */
  isEnabled(feature: AppFeature): boolean {
    return this.accessState()[feature];
  }

  /** Emits once the flags are resolved, triggering the first load when needed. */
  whenEnabled(feature: AppFeature): Observable<boolean> {
    if (!this.requested) {
      this.refresh();
    }
    return this.loaded$.pipe(
      filter((loaded) => loaded),
      take(1),
      map(() => this.isEnabled(feature))
    );
  }

  private loadAccess(): Observable<FeatureAccess> {
    return forkJoin({
      [AppFeature.SignedPunches]: this.safeFlag(this.documentProcessor.v1DocumentProcessorIsSignedPunchesVisibleGET()),
      [AppFeature.MyPunches]: this.safeFlag(this.punchBagService.v1PunchBagIsEnabledGET()),
    });
  }

  private safeFlag(request: Observable<boolean>): Observable<boolean> {
    return request.pipe(
      map((enabled) => !!enabled),
      catchError(() => of(false))
    );
  }
}
